import { type TeamChatDraft } from '@littlebird/sdk';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { LittlebirdMarkIcon } from '@/assets/icons/littlebird-mark';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { isMac } from '@/utils/capabilities';

import { firstNameOf } from './team-chat-messages';

const reasonOf = (draft: TeamChatDraft): string => {
  switch (draft.reason) {
    case 'mention':
      return `${firstNameOf(draft.reasonActorName)} mentioned you`;
    case 'teammate_post':
      return `${firstNameOf(draft.reasonActorName)} posted`;
    case 'requested':
      return 'You asked';
  }
};

// Past this many characters the text starts clamped
const LONG_DRAFT_CHARS = 280;

/** The viewer's own Littlebird draft, docked above the composer: only they see it until they send it */
export function TeamChatDraftCard({
  draft,
  isSending,
  onSend,
  onEdit,
  onDiscard,
}: {
  readonly draft: TeamChatDraft;
  readonly isSending: boolean;
  readonly onSend: () => void;
  readonly onEdit: () => void;
  readonly onDiscard: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const text = draft.text;
  const isLong = text !== null && text.length > LONG_DRAFT_CHARS;
  return (
    <YStack
      data-testid="team-chat-draft-card"
      aria-label="Your Littlebird's draft"
      className="mb-2 shrink-0 gap-1.5 rounded-xl border border-border bg-surface-hover px-3 py-2"
    >
      <XStack className="items-center gap-1.5">
        <LittlebirdMarkIcon aria-hidden className="size-4 shrink-0" />
        <span className="flex-1 text-xs text-foreground-subtle">
          {text === null
            ? 'Your Littlebird is drafting a reply…'
            : `Your Littlebird drafted a reply · ${reasonOf(draft)} · only you can see this`}
        </span>
        {text === null && (
          <Icons.loader className="size-3 animate-spin text-foreground-subtle" />
        )}
      </XStack>
      {text !== null && (
        <div className="max-h-48 overflow-y-auto">
          <p
            className={
              'no-record whitespace-pre-wrap text-sm leading-relaxed text-foreground' +
              (isLong && !showAll ? ' line-clamp-4' : '')
            }
          >
            {text}
          </p>
          {isLong && (
            <button
              type="button"
              className="text-xs text-foreground-subtle hover:text-foreground"
              onClick={() => setShowAll((shown) => !shown)}
            >
              {showAll ? 'Show less' : 'Show all'}
            </button>
          )}
        </div>
      )}
      <XStack className="items-center justify-end gap-1.5">
        {text === null ? (
          <Button size="xs" variant="ghost" onClick={onDiscard}>
            Cancel
          </Button>
        ) : (
          <>
            <span className="mr-auto text-xs text-foreground-subtle opacity-70">
              {isMac() ? '⌘' : 'Ctrl'}+Enter to send
            </span>
            <Button size="xs" variant="ghost" onClick={onDiscard}>
              Discard
            </Button>
            <Button size="xs" variant="secondary" onClick={onEdit}>
              Edit
            </Button>
            <Button
              size="xs"
              variant="default"
              isLoading={isSending}
              onClick={onSend}
            >
              Send
            </Button>
          </>
        )}
      </XStack>
    </YStack>
  );
}
